import OpenGameSDK from '@playdotfun/game-sdk';

interface EndRoundResponse {
  ok: boolean;
  pointsSaved?: number;
  skipped?: boolean;
  error?: string;
}

export class PlayFunIntegration {
  private readonly enabled: boolean;
  private readonly apiKey: string;
  private readonly gameId: string;
  private readonly serverUrl: string;
  private sdk: OpenGameSDK | null = null;
  private initialized = false;
  private ready = false;

  constructor() {
    this.enabled = import.meta.env.VITE_PLAYFUN_ENABLED === 'true';
    this.apiKey = import.meta.env.VITE_PLAYFUN_API_KEY ?? '';
    this.gameId = import.meta.env.VITE_PLAYFUN_GAME_ID ?? '';
    this.serverUrl = import.meta.env.VITE_PLAYFUN_SERVER_URL ?? 'http://localhost:8787';
  }

  async init(): Promise<void> {
    if (!this.enabled || this.initialized) return;
    this.initialized = true;

    if (!this.apiKey || !this.gameId) {
      console.warn('[PlayFun] Enabled, but missing VITE_PLAYFUN_API_KEY or VITE_PLAYFUN_GAME_ID.');
      return;
    }

    try {
      this.sdk = new OpenGameSDK({
        apiKey: this.apiKey,
        gameId: this.gameId,
        ui: {
          usePointsWidget: true,
        },
      });

      await this.sdk.init({ gameId: this.gameId });
      this.ready = true;

      if (import.meta.env.DEV) {
        const mode = window.self !== window.top ? 'iframe' : 'standalone';
        console.info(`[PlayFun] SDK initialized (${mode}) for game ${this.gameId}.`);
      }
    } catch (error) {
      console.error('[PlayFun] Failed to initialize SDK.', error);
      this.ready = false;
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  isReady(): boolean {
    return this.ready;
  }

  getSessionToken(): string | undefined {
    return this.sdk?.sessionToken;
  }

  getPlayerId(): string | undefined {
    return this.sdk?.playerId;
  }

  async login(): Promise<void> {
    if (!this.sdk || !this.ready) return;
    await this.sdk.login();
  }

  async refreshPointsAndMultiplier(): Promise<void> {
    if (!this.sdk || !this.ready) return;
    await this.sdk.refreshPointsAndMultiplier();
  }

  async submitEndRound(cookiesDelta: number, cookiesPs: number): Promise<EndRoundResponse> {
    if (!this.ready || !this.sdk) {
      return { ok: false, error: 'PlayFun SDK is not ready.' };
    }

    const sessionToken = this.sdk.sessionToken;
    const playerId = this.sdk.playerId;

    if (!sessionToken || !playerId) {
      return { ok: false, skipped: true, error: 'No active PlayFun session.' };
    }

    const response = await fetch(`${this.serverUrl}/api/playfun/end-round`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionToken,
        playerId,
        cookiesDelta,
        cookiesPs,
      }),
    });

    const payload = await response.json() as EndRoundResponse;
    if (!response.ok) {
      return { ok: false, error: payload.error ?? `HTTP ${response.status}` };
    }

    if (payload.ok) {
      await this.refreshPointsAndMultiplier();
    }

    return payload;
  }

  /** Load save state from server. Returns base64 save or null. */
  async loadSaveState(): Promise<string | null> {
    if (!this.ready || !this.sdk) return null;
    const sessionToken = this.sdk.sessionToken;
    const playerId = this.sdk.playerId;
    if (!sessionToken || !playerId) return null;

    const url = `${this.serverUrl}/api/playfun/save-state?sessionToken=${encodeURIComponent(sessionToken)}`;
    const response = await fetch(url);
    if (response.status === 404) return null;
    if (!response.ok) return null;

    const data = (await response.json()) as { ok?: boolean; save?: string };
    return data.ok && typeof data.save === 'string' ? data.save : null;
  }

  /** Save state to server. Returns true if saved. */
  async saveSaveState(encoded: string): Promise<boolean> {
    if (!this.ready || !this.sdk) return false;
    const sessionToken = this.sdk.sessionToken;
    const playerId = this.sdk.playerId;
    if (!sessionToken || !playerId) return false;

    const response = await fetch(`${this.serverUrl}/api/playfun/save-state`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken, playerId, save: encoded }),
    });
    if (!response.ok) return false;
    const data = (await response.json()) as { ok?: boolean };
    return data.ok === true;
  }
}
